import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Avatar,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack,
  Send,
  Person,
  Headset,
  ArrowUpward,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useRefresh } from "../../contexts/RefreshContext";
import theme from "../../theme/theme";
import SupportTicketService from "../../api/SupportTicketService";
import { fetchEmployeesByDesignation } from "../../api/EmployeeService";

const STATUS_CONFIG = {
  open: { bg: "#EBF5FF", color: "#1976D2", label: "Open" },
  in_progress: { bg: "#FFF8E1", color: "#F57F17", label: "In Progress" },
  resolved: { bg: "#E8F5E9", color: "#2E7D32", label: "Resolved" },
  closed: { bg: "#F5F5F5", color: "#757575", label: "Closed" },
};

const PRIORITY_CONFIG = {
  low: { bg: "#E8F5E9", color: "#2E7D32" },
  medium: { bg: "#FFF8E1", color: "#F57F17" },
  high: { bg: "#FFF3E0", color: "#E65100" },
  urgent: { bg: "#FFEBEE", color: "#C62828" },
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const messagesEndRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Editable fields
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  const loadTicket = async () => {
    try {
      const res = await SupportTicketService.fetchTicket(id);
      const data = res.data?.data || res.data || res;
      setTicket(data);
      setStatus(data.status);
      setPriority(data.priority);
    } catch (err) {
      toast.error("Failed to load ticket");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
  }, [id, refreshKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await SupportTicketService.updateTicket(id, { status, priority });
      toast.success("Ticket updated");
      loadTicket();
    } catch (err) {
      toast.error(err.message || "Failed to update ticket");
    } finally {
      setUpdating(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await SupportTicketService.replyTicket(id, replyText.trim());
      setReplyText("");
      toast.success("Reply sent");
      loadTicket();
    } catch (err) {
      toast.error(err.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography color="text.secondary">Ticket not found</Typography>
      </Box>
    );
  }

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priorityCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/support-tickets")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {ticket.ticket_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ticket.subject}
          </Typography>
        </Box>
        <Chip
          label={statusCfg.label}
          sx={{
            fontWeight: 600,
            backgroundColor: statusCfg.bg,
            color: statusCfg.color,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Messages Panel */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            {/* Messages */}
            <Box
              sx={{
                maxHeight: 500,
                overflowY: "auto",
                p: 3,
                backgroundColor: "#f6f8f8",
              }}
            >
              {ticket.messages?.length === 0 && (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No messages yet
                </Typography>
              )}
              {ticket.messages?.map((msg) => {
                const isCustomer = msg.sender_type === "customer";
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent: isCustomer ? "flex-start" : "flex-end",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "70%",
                        display: "flex",
                        flexDirection: isCustomer ? "row" : "row-reverse",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: isCustomer ? "#e3f2fd" : theme.palette.primary.main,
                          color: isCustomer ? "#1976d2" : "#fff",
                        }}
                      >
                        {isCustomer ? (
                          <Person sx={{ fontSize: 18 }} />
                        ) : (
                          <Headset sx={{ fontSize: 18 }} />
                        )}
                      </Avatar>
                      <Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            backgroundColor: isCustomer ? "#fff" : theme.palette.primary.main,
                            color: isCustomer ? "text.primary" : "#fff",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              display: "block",
                              mb: 0.5,
                              color: isCustomer ? "text.secondary" : "rgba(255,255,255,0.8)",
                            }}
                          >
                            {isCustomer ? "Customer" : "Support Team"}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {msg.message}
                          </Typography>
                        </Paper>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ mt: 0.5, display: "block", px: 1 }}
                        >
                          {new Date(msg.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* Reply Input */}
            {ticket.status !== "closed" && (
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleReply}
                  disabled={!replyText.trim() || sending}
                  sx={{
                    borderRadius: 3,
                    minWidth: 48,
                    height: 40,
                    backgroundColor: theme.palette.primary.main,
                  }}
                >
                  {sending ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    <Send sx={{ fontSize: 20 }} />
                  )}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <Paper sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Ticket Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Info rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {ticket.customer?.name || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ textTransform: "capitalize" }}
                >
                  {ticket.category}
                </Typography>
              </Box>

              {ticket.project && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Associated Lift
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ticket.project.customer_name || `Lift #${ticket.project.id}`}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(ticket.created_at).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>

              <Divider />

              {/* Status */}
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                fullWidth
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>

              {/* Priority */}
              <TextField
                select
                size="small"
                label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                fullWidth
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>

              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={
                  updating ||
                  (status === ticket.status && priority === ticket.priority)
                }
                fullWidth
                sx={{
                  borderRadius: theme.button.borderRadius,
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                {updating ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Update Ticket"
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TicketDetailPage;
